// ============================================================
// ViewingPane.tsx
// The full CASE PAGE. Stage 1 redesign (approved mockup "CA132B" +
// casevo-case-page-structure Option A): HERO (CaseHero) + GROUPED left
// section-nav (CaseSectionNav, Settings/Admin nav style, mobile pills) +
// the OVERVIEW landing (CaseOverview). Every EXISTING section component is
// mounted unchanged in the new frame (one active panel at a time, exactly as
// before) — sections are redesigned in later stages. Data load, the setup
// chain (completedSections / unlock / mark-complete-and-continue), every
// autosave + flush, access gates, presence + live sync are unchanged: this
// file only re-lays-out navigation + header + landing.
// ============================================================
import { useCaseNavFold } from '../case/caseNavFold'
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo, navigateWithin } from '../../navigation/navigate'
import type { Place } from '../../navigation/placeUrl'
import { useShallow } from 'zustand/react/shallow'
import { useSectionCompletion, CompletableSection, COMPLETABLE_SECTIONS } from '../../store/useSectionCompletion'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { flushClientAutoSave } from '../../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../../hooks/useClientNotesAutoSave'
import { flushWitnessAutoSave } from '../../hooks/useWitnessAutoSave'
import { flushDeadlineAutoSave } from '../../hooks/useCaseDeadlineAutoSave'
import { ONBOARDING_PROMPTS_SEEN_KEY } from '../../hooks/useOnboardingPrompt'
import { VIEWING_PANE_HEADINGS } from '../../constants/viewingPane.constants'
import { ViewingPaneToggles, ViewingPaneHeading, CaseSectionKey } from '../../types/case.types'
import { getVisibleSequentialKeys, getPrevSequentialKey, getSectionLabel, sectionHasData, getSectionStatus, type SectionStatus } from '../../utils/sectionFlow'
import { validateCase } from '../../utils/validation'
import { useCanViewCaseHistory } from '../../hooks/useCanViewCaseHistory'
import { usePresenceRoom, useFieldOwners } from '../../hooks/usePresence'
import { editingLabel } from '../shared/CardPresence'
import { flushLiveDocSnapshots } from '../shared/liveDocFlush'
import CaseHero from '../case/CaseHero'
import CaseSectionNav from '../case/CaseSectionNav'
import CaseOverview from '../case/CaseOverview'
import { CASE_CARD, CaseStatusMark as CaseStatusMarkBase, todayYmd, daysBetween, ymdOf } from '../case/caseUi'
import { CASE_NAV_INDEX } from '../../constants/caseNav.constants'
import { CaseSectionFrameContext } from '../case/sections/sectionKit'
import HeadingTogglePanel from './HeadingTogglePanel'
import CaseListScreen from './CaseListScreen'
import AppHeader from './AppHeader'
import BackButton from './BackButton'
import CaseLoadingSkeleton from '../case/CaseLoadingSkeleton'
import { SidebarHamburger } from './Sidebar'
import ValidationSummary from '../shared/ValidationSummary'
import AutomaticSection from '../sections/AutomaticSection'
import CaseHistorySection from '../sections/CaseHistorySection'
import ClientSourceSection from '../sections/ClientSourceSection'
import ClientDetailsSection from '../sections/ClientDetailsSection'
import AccidentDetailsSection from '../sections/AccidentDetailsSection'
import InjuryDetailsSection from '../sections/InjuryDetailsSection'
import DefendantSection from '../sections/DefendantSection'
import WitnessesSection from '../sections/WitnessesSection'
import DeadlinesSection from '../sections/DeadlinesSection'
import TPISection from '../sections/TPISection'
import LinkedCasesSection from '../sections/LinkedCasesSection'
import CaseSettlementSection from '../settlement/CaseSettlementSection'
import MedicalAgencySection from '../sections/MedicalAgencySection'
import MedicalExpertSection from '../sections/MedicalExpertSection'
import TPSSection from '../sections/TPSSection'
import DocumentsSection from '../sections/DocumentsSection'
import CaseActivitySection, { useCaseActivityWip } from '../sections/CaseActivitySection'
import FundingSection from '../sections/FundingSection'
import CaseBillingSection from '../billing/CaseBillingSection'
import { caseName as caseDisplayName, caseRef as caseDisplayRef } from '../../utils/caseName'
import SettingsSection from '../sections/SettingsSection'
import { requiredFieldsFor } from '../shared/requiredFields'
const SECTION_MAP: Record<string, React.ReactNode> = {
  automatic:       <AutomaticSection />,
  clientSource:    <ClientSourceSection />,
  clientDetails:   <ClientDetailsSection />,
  accidentDetails: <AccidentDetailsSection />,
  injuryDetails:   <InjuryDetailsSection />,
  witnesses:       <WitnessesSection />,
  deadlines:       <DeadlinesSection />,
  defendant:       <DefendantSection />,
  tpi:             <TPISection />,
  linkedCases:     <LinkedCasesSection />,
  medicalAgency:   <MedicalAgencySection />,
  medicalExpert:   <MedicalExpertSection />,
  tps:             <TPSSection />,
  funding:         <FundingSection />,
  settings:        <SettingsSection />,
}
// Stage 2 (batch 1) + Stage 3 (batch 2): sections redesigned to the view/edit card pattern draw
// their own head, cards and footer (no shared CASE_CARD wrapper).
// Stage 4 (batch 3): Documents + the new Activity feed.
// Final pass: Case control, Defendant, TPI, Funding, Linked cases, History —
// EVERY case section now draws its own frame.
const SELF_FRAMED = new Set<string>(['automatic', 'clientSource', 'clientDetails', 'accidentDetails', 'injuryDetails', 'witnesses', 'defendant', 'tpi', 'linkedCases', 'medicalAgency', 'medicalExpert', 'tps', 'deadlines', 'documents', 'funding', 'activity', 'history', 'billing', 'settlement'])
// Field-presence namespace (fieldKey prefix) → case section key, for the
// nav's "someone is editing here" dot.
const FIELDKEY_SECTION: Record<string, string> = {
  clientDetails: 'clientDetails', accident: 'accidentDetails', injuries: 'injuryDetails', witness: 'witnesses',
  clientSource: 'clientSource', defendant: 'defendant', tpi: 'tpi', tps: 'tps', medicalAgency: 'medicalAgency',
  medicalExpert: 'medicalExpert', funding: 'funding', deadline: 'deadlines',
}
// Whisper-light fade on section swap. When swapKey changes, the wrapper
// starts at opacity-0 and transitions to opacity-100 over 150ms. Purely
// cosmetic — the content is mounted + interactive the whole time (opacity
// never blocks input), so a daily user isn't slowed down; it just avoids a
// hard snap between sections.
const FadeSwap: React.FC<{ swapKey: string; children: React.ReactNode }> = ({ swapKey, children }) => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    setVisible(false)
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [swapKey])
  return (
    // flex-1 flex flex-col: part of the panel-fill chain (panel → FadeSwap
    // → section wrapper → section), so a short section can stretch to the
    // panel's height and pin its "Mark complete" button to the bottom.
    <div className={`flex-1 flex flex-col transition-opacity duration-150 ease-out ${visible ? 'opacity-100' : 'opacity-0'}`}>
      {children}
    </div>
  )
}
const CaseStatusMarkInline: React.FC<{ status: SectionStatus }> = ({ status }) => <CaseStatusMarkBase status={status} size={14} />
// Section-head status chip (mockup .sst) — from the same nav status.
const SST = 'inline-flex items-center gap-[6px] text-[11.5px] font-bold normal-case tracking-normal pl-[6px] pr-[9px] py-[3px] rounded-full'
const SECTION_CHIP: Record<SectionStatus, React.ReactNode> = {
  complete: <span className={`${SST} bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]`}><CaseStatusMarkInline status="complete" />Complete</span>,
  in_progress: <span className={`${SST} bg-casevo-input text-casevo-text-soft border border-casevo-border-soft`}><CaseStatusMarkInline status="in_progress" />In progress</span>,
  empty: <span className={`${SST} bg-casevo-input text-casevo-text-soft border border-casevo-border-soft`}><CaseStatusMarkInline status="empty" />Not started</span>,
  attention: <span className={`${SST} bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]`}><CaseStatusMarkInline status="attention" />Needs attention</span>,
  locked: null,
  neutral: null,
}
// forceLoading: App's ?caseId cold-open renders the pane in its loading
// state (shell + skeleton) before the store's isCaseLoading flips on.
const ViewingPane: React.FC<{ forceLoading?: boolean }> = ({ forceLoading = false }) => {
// (★ audit, perf) shallow-selected — no longer re-renders on unrelated store writes.
const { viewingPaneToggles, userRole, currentCase, isCaseLoading } =
    useCasevoStore(useShallow((s) => ({
      viewingPaneToggles: s.viewingPaneToggles, userRole: s.userRole, currentCase: s.currentCase, isCaseLoading: s.isCaseLoading,
    })))
  // Case History Visibility (Stage 2) — whether THIS user may see THIS
  // case's History section, per the firm setting. UX suppression only;
  // the backend still enforces (403). Used to hide both the section and
  // its toggle entry so an excluded viewer never hits an error banner.
  const canViewCaseHistory = useCanViewCaseHistory()
  // Presence ROOM for the case surface: `case:<id>` when a case is open,
  // null on the case list. Joins/leaves via the shared singleton. (The
  // focus-delegation listener now lives app-wide in App, so it's not here.)
  usePresenceRoom(currentCase ? `case:${currentCase.id}` : null)
  const [togglePanelOpen, setTogglePanelOpen] = useState(false)
  // The case detail is a section-nav + single panel. activeCaseSection —
  // the currently-shown section — is STORE state (Stage 4) so section
  // components can advance it: "Mark complete and continue" sets the next
  // section via advanceCaseSection, swapping the panel. Deep-links and the
  // nav also set it through the same store slot.
  const activeCaseSection = useCasevoStore((s) => s.activeCaseSection)
  // ★ ONE case-menu rule on EVERY section (case/caseNavFold): folded to an
  // icon rail automatically when the content area is narrower than ~1200px,
  // unless the user has chosen (rail chevron / "Fold menu") — their choice is
  // remembered per user and wins. The page itself is the wide (1560) column
  // on every section; cards keep sensible widths inside.
  const [navCompact, toggleNavCompact, contentAreaRef] = useCaseNavFold()
  const setActiveCaseSection = useCasevoStore((s) => s.setActiveCaseSection)
  // Section-flow completion state. completedSections drives the ✓ status
  // + progress bar + all-complete banner; unlockedSections drives the 🔒
  // locked nav rows (a section stays locked until its predecessor is
  // marked complete). markIncomplete is no longer read here (the old
  // "Edit details" affordance lived in the vertical-scroll wrapper).
  const loadForCase = useSectionCompletion((s) => s.loadForCase)
  const completedSections = useSectionCompletion((s) => s.completedSections)
  const unlockedSections = useSectionCompletion((s) => s.unlockedSections)
  // Witness + document counts live in their own store slices (not on the
  // Case object), so they feed sectionHasData for the in-progress-vs-empty
  // status of those two sections.
  const witnesses = useCasevoStore((s) => s.witnesses)
  const files = useCasevoStore((s) => s.files)
  // Hydrate completion + unlock state from localStorage on case
  // open. loadForCase no-ops when the requested id matches what's
  // already loaded so re-renders triggered by other store mutations
  // don't wipe in-progress UI.
  useEffect(() => {
    // Seed completion from the CASE's shared completedSections (source of
    // truth) so every user viewing the case sees the same completed
    // sections — not each user's own localStorage.
    if (currentCase) loadForCase(currentCase.id, currentCase.completedSections)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCase?.id, loadForCase])
  // The ordered, filtered list of sections shown in the nav — the SAME
  // filter the old vertical-scroll loop used: toggle-on, admin-gated, and
  // the per-case History-visibility gate. Order follows VIEWING_PANE_HEADINGS.
  const visibleHeadings = VIEWING_PANE_HEADINGS.filter((heading) => {
    if (!viewingPaneToggles[heading.key as keyof ViewingPaneToggles]) return false
    if (heading.adminOnly && userRole !== 'admin') return false
    // Case History Visibility gate — hide History for viewers the firm
    // setting excludes (backend still enforces; this is UX suppression).
    if (heading.key === 'history' && !canViewCaseHistory) return false
    return true
  })
  const visibleKeys = visibleHeadings.map((h) => h.key)
  // Stable string of the visible keys so the reconcile effect re-runs when
  // the visible SET changes (e.g. the user toggles a section off), not on
  // every currentCase rebuild.
  const visibleKeysString = visibleKeys.join(',')
  // Deep-link + guided default + keep-valid, one effect:
  //   • DEEP-LINK wins: routeByItemType writes caseInitialSection to the
  //     store BEFORE setCurrentCaseById; consume it INTO activeCaseSection
  //     (panel swap) and clear it so it can't leak across cases.
  //   • GUIDED DEFAULT on a fresh case (id changed) or if the current
  //     selection is no longer visible: land on the first VISIBLE, UNLOCKED,
  //     INCOMPLETE completable section — "where you left off". If none (a
  //     finalised case, all complete), fall back to the first visible
  //     section (review mode).
  //   • Otherwise (same case, valid selection) leave it alone — so an
  //     in-session switch isn't overridden by a re-render.
  // Completion is read fresh via getState() because loadForCase (the effect
  // above) hydrates the sets on the SAME commit, before the subscribed
  // values re-render. Dep is currentCase?.id (not the object) so
  // keystroke-driven case rebuilds don't re-fire this mid-edit.
  const caseInitialSection = useCasevoStore((s) => s.caseInitialSection)
  const lastCaseIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentCase) { lastCaseIdRef.current = null; return }
    const store = useCasevoStore.getState()
    if (caseInitialSection && visibleKeys.includes(caseInitialSection)) {
      store.setActiveCaseSection(caseInitialSection)
      store.setCaseInitialSection(null)
      lastCaseIdRef.current = currentCase.id
      return
    }
    // A deep-link to a section that's hidden/not visible here is dropped
    // (never left in the store to fire on a LATER case that does show it).
    if (caseInitialSection) store.setCaseInitialSection(null)
    const idChanged = lastCaseIdRef.current !== currentCase.id
    lastCaseIdRef.current = currentCase.id
    const current = store.activeCaseSection
    // Stage 1: a freshly-opened case (or an invalid selection) lands on the
    // OVERVIEW — its "Continue: <first unlocked, incomplete section>" CTA
    // replaces the old auto-jump to that section. Deep-links still win (above).
    const valid = current === 'overview' || (!!current && visibleKeys.includes(current))
    if (idChanged) {
      // ★ Per-case section memory (mirror of the client clientSectionMemo):
      // ViewingPane UNMOUNTS when Settings / another screen takes over, and
      // Back re-fetches the case (restoreView nulls currentCase first), so
      // this ref reads "changed" on every return. Restore the section the
      // user was on when it's THIS case and still visible; only a genuinely
      // different case (or a now-hidden section) lands on the Overview.
      const memo = store.caseSectionMemo
      const remembered = memo && memo.caseId === currentCase.id
        && (memo.section === 'overview' || visibleKeys.includes(memo.section))
        ? memo.section
        : null
      store.setActiveCaseSection(remembered ?? 'overview')
    } else if (!valid) {
      store.setActiveCaseSection('overview')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCase?.id, caseInitialSection, visibleKeysString])
  // Computed once per render — visible sequential sections in order.
  // Used here to derive lockedReason (the previous section's label).
  const visibleSequential = currentCase
    ? getVisibleSequentialKeys(viewingPaneToggles, userRole)
    : []
  // True when every visible sequential section is in the
  // completedSections set. Drives the bottom-of-page completion
  // banner. When length is 0 (user toggled everything off) we
  // deliberately return false — there's nothing to complete.
  const allSectionsComplete = visibleSequential.length > 0
    && visibleSequential.every((key) => completedSections.has(key))
  // The heading config for the section currently shown in the panel.
  // null when nothing's selected yet (pre-reconcile) or the case is closed.
  const activeHeading = visibleHeadings.find((h) => h.key === activeCaseSection) ?? null
  const isOverview = activeCaseSection === 'overview'
  // Per-section nav status + lock reason. Composed from the EXISTING
  // signals — completedSections (✓), unlockedSections (🔒), validateCase
  // (⚠️) — plus the new sectionHasData (◐ vs ○). No new completion logic.
  const completableSet = new Set<string>(COMPLETABLE_SECTIONS)
  const validationErrors = currentCase ? validateCase(currentCase) : {}
  const witnessCount = currentCase ? (witnesses[currentCase.id]?.length ?? 0) : 0
  const documentCount = currentCase ? (files[currentCase.id]?.length ?? 0) : 0
  const statusByKey: Partial<Record<ViewingPaneHeading, SectionStatus>> = {}
  const lockReasonByKey: Partial<Record<ViewingPaneHeading, string>> = {}
  if (currentCase) {
    for (const heading of visibleHeadings) {
      const key = heading.key
      const isCompletable = completableSet.has(key)
      const ck = key as CompletableSection
      // Unlocked if the store says so OR (derived) it's the first visible
      // sequential section / its visible predecessor is complete. The store's
      // unlock set is only grown at mark-complete time against the chain
      // visible THEN — re-showing a hidden section left it locked forever
      // ("Complete A first" with A already complete).
      const prevKey = isCompletable ? getPrevSequentialKey(ck, visibleSequential) : undefined
      const isUnlocked = isCompletable
        ? unlockedSections.has(ck) || !prevKey || completedSections.has(prevKey)
        : true
      statusByKey[key] = getSectionStatus({
        isCompletable,
        isComplete: isCompletable && completedSections.has(ck),
        isUnlocked,
        hasData: isCompletable && sectionHasData(ck, currentCase, { witnessCount, documentCount }),
        hasErrors: (validationErrors[key]?.length ?? 0) > 0,
      })
      if (isCompletable && !isUnlocked) {
        const prev = getPrevSequentialKey(ck, visibleSequential)
        lockReasonByKey[key] = prev ? `Complete ${getSectionLabel(prev as ViewingPaneHeading)} first` : 'Locked'
      }
    }
  }
  // ── Nav extras (display only) ──
  // Counts beside a row, and Deadlines shows "!" when one is overdue.
  const caseDeadlinesMap = useCasevoStore((s) => s.caseDeadlines)
  const openDeadlines = currentCase ? (caseDeadlinesMap[currentCase.id] ?? []).filter((d) => !d.completed) : []
  const today = todayYmd()
  const overdueCount = openDeadlines.filter((d) => { const y = ymdOf(d.dueDate); return !!y && daysBetween(today, y) < 0 }).length
  // Activity: this case's unbilled WIP (mockup nav "£253"), whole pounds.
  const activityWip = useCaseActivityWip(currentCase?.id ?? null, currentCase?.client?.id ?? null, visibleKeys.includes('activity'))
  const baseSubByKey: Partial<Record<string, React.ReactNode>> = {
    activity: activityWip !== null && activityWip > 0 ? `£${Math.floor(activityWip / 100).toLocaleString('en-GB')}` : undefined,
    // Invoicing Stage 1 — Billing row hint (mockup "WIP £1,023").
    billing: activityWip !== null && activityWip > 0 ? `WIP £${Math.floor(activityWip / 100).toLocaleString('en-GB')}` : undefined,
    witnesses: witnessCount > 0 ? String(witnessCount) : undefined,
    documents: documentCount > 0 ? String(documentCount) : undefined,
    deadlines: overdueCount > 0 ? `${overdueCount} overdue` : openDeadlines.length > 0 ? `${openDeadlines.length} open` : undefined,
  }
  // ── "Someone is editing here" nav dot (shared field presence) ──
  // A colleague's focused fieldKey → the section it belongs to (by the
  // fieldKey namespace every section's FormFields use).
  const fieldOwners = useFieldOwners()
  const selfId = useCasevoStore((s) => s.authUser?.id)
  const editorsBySection: Record<string, typeof fieldOwners> = {}
  for (const o of fieldOwners) {
    if (o.userId === selfId) continue
    const ns = o.fieldKey.split('.')[0]
    const section = FIELDKEY_SECTION[ns]
    if (!section) continue
    const list = (editorsBySection[section] ??= [])
    if (!list.some((x) => x.userId === o.userId)) list.push(o)
  }
  const navSubByKey: Partial<Record<string, React.ReactNode>> = { ...baseSubByKey }
  for (const [section, eds] of Object.entries(editorsBySection)) {
    const label = editingLabel(eds)
    navSubByKey[section] = (
      <span className="inline-flex items-center gap-[6px]">
        {baseSubByKey[section]}
        <span role="img" aria-label={label} title={label} className="w-[8px] h-[8px] rounded-full ring-2 ring-casevo-surface shrink-0" style={{ backgroundColor: eds[0].avatarColour || '#2d6a4f' }} />
      </span>
    )
  }
  const navStatusByKey: Partial<Record<string, SectionStatus>> = {
    ...statusByKey,
    ...(overdueCount > 0 ? { deadlines: 'attention' as SectionStatus } : {}),
  }
  // Sections the viewer may see but has toggled off (nav footer count).
  const hiddenCount = VIEWING_PANE_HEADINGS.filter((h) =>
    !(h.adminOnly && userRole !== 'admin') && !(h.key === 'history' && !canViewCaseHistory)
    && !viewingPaneToggles[h.key as keyof ViewingPaneToggles]).length
  // Drain every pending auto-save stream in parallel — case-wide
  // (useAutoSave), linked-client (useClientAutoSave), client-notes,
  // per-witness, and per-deadline. Called before ANY navigation that
  // unmounts editable content: leaving the case (goBack) AND — new this
  // stage — switching sections in the nav, so the outgoing section's
  // sub-second debounce window is persisted before its panel unmounts.
  // Live (Yjs) editors' pending readable-copy snapshots go first, so they
  // ride these flushes instead of landing after the panel unmounts.
  const flushAllPending = async (): Promise<unknown> => {
    await flushLiveDocSnapshots()
    return Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
  }
  // Switch the visible section. Flush first so a pending edit in the
  // section we're leaving isn't lost when its panel unmounts (belt-and-
  // braces: case fields already live in the store and would save anyway,
  // but witness/deadline/client streams settle here too). No-op when the
  // clicked section is already active.
  const handleSectionNavClick = async (key: CaseSectionKey): Promise<void> => {
    if (key === activeCaseSection) return
    // Locked rows are non-clickable in the nav; guard the Overview's chain
    // chips + links the same way.
    if (key !== 'overview' && statusByKey[key] === 'locked') return
    await flushAllPending()
    // A section is a place of its own: Back returns to the one you were on.
    await navigateWithin(() => setActiveCaseSection(key))
  }
  // The sensible parent when there's no history to go Back to (a case
  // opened fresh from a link): the screen it was opened from, else the list.
  const parentPlace = (): Place => {
    switch (useCasevoStore.getState().caseOpenedFrom) {
      case 'clientSearch': return { view: 'clientSearch' }
      case 'documentSearch': return { view: 'documentSearch' }
      case 'calendar': return { view: 'calendar' }
      case 'dashboard': return { view: 'dashboard' }
      case 'taskList': return { view: 'tasks' }
      default: return { view: 'caseList' }
    }
  }
  // Wordmark = "go home" (the parent screen) — a navigation like any other
  // (navigateTo flushes pending edits first and records the step).
  const goHome = async (): Promise<void> => {
    await navigateTo(parentPlace())
  }
  // The open case was DELETED from the hero's ⋯ menu: leave it, replacing
  // its history entry (its pending saves must not fire — no flush).
  const leaveCase = (): void => {
    void navigateTo(parentPlace(), { replace: true, skipFlush: true })
  }
  // First time the user opens any case detail, mark onboarding prompts
  // as seen. Children's mount-snapshot has already captured the
  // "show prompts" decision by the time this effect runs (effects fire
  // after children render), so the user still sees the prompts on this
  // visit — they just won't appear on the next case.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(ONBOARDING_PROMPTS_SEEN_KEY) === null) {
      window.localStorage.setItem(ONBOARDING_PROMPTS_SEEN_KEY, 'true')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // The page's topbar (portalled into App's topbar slot). Hoisted so the
  // LOADING state renders it too — without it the topbar portal is empty and
  // the topbar visibly vanishes while a case loads.
  const header = (
    <AppHeader
      onWordmarkClick={() => { void goHome() }}
      compactLogoOnMobile
      hideSubtitleOnMobile
      leftHamburger={<SidebarHamburger />}
      leftSlot={
        // ← Back = the place you were actually on; hidden when the case was
        // opened fresh (no in-app history).
        <BackButton fallback={parentPlace()} className="text-casevo-light hover:text-white text-sm transition-colors flex items-center gap-1">
          ← <span className="hidden sm:inline">Back</span>
        </BackButton>
      }
    />
  )
  // No case yet but one is being fetched (Full case before the quick-view's
  // fetch returned, Back, cold-open, sidebar/dashboard/notification opens):
  // keep the SHELL — topbar header + the case column — and show a hero/nav/
  // content skeleton in the content area only. Never a bare spinner (which
  // dropped the AppHeader) and never a CaseListScreen flash.
  if (!currentCase) {
    if (isCaseLoading || forceLoading) {
      return (
        <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
          {header}
          <div className="max-w-[1560px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[26px] pb-[44px]">
            <CaseLoadingSkeleton />
          </div>
        </div>
      )
    }
    return <CaseListScreen />
  }
  return (
    <div ref={contentAreaRef} className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      {/* Nav */}
      {header}
      {/* Case content — a WIDE column (1560) on every case section, so the
          page uses the room (Settlement's figures + preview, wide tables);
          the case menu folds by the shared rule above, and each section keeps
          sensible widths inside its cards. */}
      <div className="max-w-[1560px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[26px] pb-[44px]">
        <CaseHero
          allSectionsComplete={allSectionsComplete}
          incompleteCount={requiredFieldsFor(currentCase, caseDeadlinesMap[currentCase.id], lockReasonByKey, visibleKeys, activeHeading?.key ?? null).length}
          onCaseDeleted={leaveCase}
          onCaseMutated={() => {
            // Re-read the open case after a ⋯-menu change (status, link…):
            // flush pending edits first, then the same loader the case opened
            // with (a refresh of an open case never flashes the spinner).
            const id = currentCase.id
            void flushAllPending().then(() => useCasevoStore.getState().setCurrentCaseById(id))
          }}
        />
        <HeadingTogglePanel isOpen={togglePanelOpen} onClose={() => setTogglePanelOpen(false)} />
        {/* Required fields still to fill in (amber card; folds away when done). */}
        <ValidationSummary onGo={(key) => { void handleSectionNavClick(key) }} lockReasonByKey={lockReasonByKey} visibleKeys={visibleKeys} openSection={activeHeading?.key ?? null} />
        {/* Grouped section nav + single panel. Only the ACTIVE panel is
            mounted (as before); Overview is the landing panel. */}
        <div className={`mt-[14px] md:mt-[22px] grid grid-cols-1 ${navCompact ? 'md:grid-cols-[52px_minmax(0,1fr)]' : 'md:grid-cols-[232px_minmax(0,1fr)]'} gap-[16px] md:gap-[26px] items-start`}>
          <div className="min-w-0 md:self-stretch">
            <CaseSectionNav
              visibleKeys={visibleKeys}
              active={activeCaseSection}
              statusByKey={navStatusByKey}
              lockReasonByKey={lockReasonByKey}
              subByKey={navSubByKey}
              hiddenCount={hiddenCount}
              onSelect={(key) => { void handleSectionNavClick(key) }}
              onManageSections={() => setTogglePanelOpen(true)}
              compact={navCompact}
              onToggleCompact={toggleNavCompact}
            />
          </div>
          <div className="min-w-0 flex flex-col">
            {isOverview && (
              <FadeSwap swapKey="overview">
                <CaseOverview
                  visibleSequential={visibleSequential}
                  statusByKey={statusByKey}
                  completedSections={completedSections}
                  allSectionsComplete={allSectionsComplete}
                  canOpen={(key) => key === 'overview' || (visibleKeys.includes(key) && statusByKey[key as ViewingPaneHeading] !== 'locked')}
                  onOpen={(key) => { void handleSectionNavClick(key) }}
                  canViewHistory={canViewCaseHistory}
                />
              </FadeSwap>
            )}
            {!isOverview && activeHeading && (
              // FadeSwap keyed on the section key → the same 150ms fade.
              <FadeSwap swapKey={activeHeading.key}>
                {/* ★ keyed by CASE too (case-page audit): switching case on the
                    same section remounts it, so per-section local state (an
                    open edit card + its Cancel snapshot, History's appended
                    pages) can never carry over into the next case. */}
                <div key={currentCase.id} id={`section-${activeHeading.key}`} className="flex-1 flex flex-col">
                  {SELF_FRAMED.has(activeHeading.key) ? (
                    // Stage 2 sections render their OWN head (+ Edit all),
                    // cards and footer; the frame context hands them the
                    // group eyebrow + status chip.
                    <CaseSectionFrameContext.Provider value={{
                      group: CASE_NAV_INDEX[activeHeading.key]?.group ?? 'Case',
                      chip: SECTION_CHIP[statusByKey[activeHeading.key] ?? 'neutral'],
                    }}>
                      <div className="mt-[2px]">
                        {activeHeading.key === 'documents' ? <DocumentsSection caseId={currentCase.id} />
                          : activeHeading.key === 'activity' ? <CaseActivitySection />
                          : activeHeading.key === 'billing' ? <CaseBillingSection caseId={currentCase.id} caseName={caseDisplayName(currentCase)} caseRef={caseDisplayRef(currentCase)} />
                          : activeHeading.key === 'settlement' ? <CaseSettlementSection caseId={currentCase.id} />
                            : activeHeading.key === 'history' ? <CaseHistorySection caseId={currentCase.id} />
                              : SECTION_MAP[activeHeading.key]}
                      </div>
                    </CaseSectionFrameContext.Provider>
                  ) : (<>
                  {/* Section head (group eyebrow + status + title). The
                      section body below is the EXISTING component, unchanged,
                      in a standard surface card (History brings its own). */}
                  <div className="mt-[2px] mb-[14px]">
                    <div className="flex items-center gap-[8px] text-[11px] tracking-[0.16em] uppercase font-bold text-casevo-text-soft">
                      {CASE_NAV_INDEX[activeHeading.key]?.group ?? 'Case'}
                      {SECTION_CHIP[statusByKey[activeHeading.key] ?? 'neutral']}
                    </div>
                    <h2 className="mt-[3px] mb-0 text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest">
                      {CASE_NAV_INDEX[activeHeading.key]?.label ?? activeHeading.label}
                    </h2>
                  </div>
                  {activeHeading.key === 'history' ? (
                    <CaseHistorySection caseId={currentCase.id} />
                  ) : (
                    <div className={`${CASE_CARD} overflow-hidden`}>
                      {activeHeading.key === 'documents' ? (
                        <DocumentsSection caseId={currentCase.id} />
                      ) : (
                        SECTION_MAP[activeHeading.key]
                      )}
                    </div>
                  )}
                  </>)}
                </div>
              </FadeSwap>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default ViewingPane
